import { type AnnualCashFlow } from '@fruitful/financial-calculators';
import { formatDollars } from '@fruitful/fruitils';
import { useMemo } from 'react';
import { Chart } from 'react-google-charts';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    H5Emphasized,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useAnnualCashFlowTotals } from '../hooks/useAnnualCashFlowTotals';
import {
    getCalendarYear,
    getYearIndex,
    RETIREMENT_CALCULATOR_COLORS,
} from '../utils/retirementCalculatorUtils';

interface AnnualCashFlowAssetGrowthProps {
    annualCashFlows: AnnualCashFlow[];
    selectedYear: number;
    onYearSelect?: (year: number) => void;
}

export const AnnualCashFlowAssetGrowth = ({
    annualCashFlows,
    selectedYear,
    onYearSelect,
}: AnnualCashFlowAssetGrowthProps) => {
    // Transform data for the line chart
    const chartData = useMemo(() => {
        if (annualCashFlows.length === 0) {
            return [['Year', 'Total Assets']];
        }

        const data: Array<Array<string | number>> = [['Year', 'Total Assets']];

        annualCashFlows.forEach((cashFlow) => {
            const calendarYear = getCalendarYear(cashFlow.year);
            const assetTotals = cashFlow.endingAssets.reduce(
                (sum, account) => sum + account.balance,
                0,
            );
            data.push([calendarYear, assetTotals]);
        });

        return data;
    }, [annualCashFlows]);

    // Get current year data for the data panel
    const totals = useAnnualCashFlowTotals(annualCashFlows, selectedYear);
    const selectedYearIndex = getYearIndex(selectedYear);
    const currentYearData = useMemo(() => {
        const cashFlow = annualCashFlows[selectedYearIndex];

        if (!totals || !cashFlow) {
            return null;
        }

        return {
            ...cashFlow,
            ...totals,
        };
    }, [totals, annualCashFlows, selectedYear]);

    // https://developers.google.com/chart/interactive/docs/gallery/linechart#configuration-options
    const chartOptions = {
        hAxis: {
            title: 'Calendar Year',
            format: '####',
        },
        vAxis: {
            title: 'Total Assets ($)',
            format: 'currency',
        },
        legend: { position: 'none' },
        colors: [RETIREMENT_CALCULATOR_COLORS.ASSETS],
        lineWidth: 3,
        pointSize: 8,
        backgroundColor: 'transparent',
        enableInteractivity: true,
    };

    return (
        <XStack gap={24} alignItems="flex-start">
            {/* Left side - Chart */}
            <YStack flex={1} gap={16}>
                <View
                    width="100%"
                    height={500}
                    borderWidth={1}
                    borderColor="$gray400"
                    borderRadius={8}
                    backgroundColor="$white"
                    overflow="hidden"
                >
                    {annualCashFlows.length > 0 ? (
                        <Chart
                            chartType="LineChart"
                            width="100%"
                            height="100%"
                            data={chartData}
                            options={chartOptions}
                            chartEvents={[
                                {
                                    eventName: 'select',
                                    callback: ({ chartWrapper }) => {
                                        if (!chartWrapper || !onYearSelect)
                                            return;

                                        const chart = chartWrapper.getChart();

                                        const selection = chart.getSelection();
                                        if (selection.length > 0) {
                                            const selectedPoint = selection[0];
                                            if (
                                                selectedPoint?.row !== undefined
                                            ) {
                                                // Update the year selection when chart is clicked. Row is the index, so convert back to Year value.
                                                onYearSelect(
                                                    ((selectedPoint.row ?? 0) +
                                                        1) as number,
                                                );
                                            }
                                        }
                                    },
                                },
                            ]}
                            loader={
                                <View
                                    alignItems="center"
                                    justifyContent="center"
                                    height="100%"
                                >
                                    <BodyMediumRegular color="$gray600">
                                        Loading chart…
                                    </BodyMediumRegular>
                                </View>
                            }
                        />
                    ) : (
                        <View
                            alignItems="center"
                            justifyContent="center"
                            height="100%"
                        >
                            <BodyMediumRegular color="$gray600">
                                No data available
                            </BodyMediumRegular>
                        </View>
                    )}
                </View>
            </YStack>

            {/* Right side - Summary Panel */}
            <YStack
                width="33%"
                gap={16}
                padding={20}
                backgroundColor="$white"
                borderRadius={12}
                borderWidth={1}
                borderColor="$gray400"
                maxHeight={600}
                overflow="scroll"
            >
                <H5Emphasized>
                    Year: {getCalendarYear(selectedYear)}
                </H5Emphasized>

                {currentYearData && (
                    <YStack gap={16}>
                        <YStack gap={8}>
                            <H5Emphasized>Total Assets</H5Emphasized>
                            <BodyMediumRegular
                                color={RETIREMENT_CALCULATOR_COLORS.ASSETS}
                            >
                                {formatDollars(
                                    currentYearData.totalEndingAssets,
                                )}
                            </BodyMediumRegular>
                        </YStack>

                        <YStack gap={12}>
                            <H5Emphasized>Asset Breakdown</H5Emphasized>
                            {currentYearData.endingAssets.map(
                                (account, index) => (
                                    <YStack
                                        key={index}
                                        gap={4}
                                        padding={12}
                                        backgroundColor="$gray100"
                                        borderRadius={8}
                                    >
                                        <BodyMediumRegular>
                                            {account.id} ({account.owner})
                                        </BodyMediumRegular>
                                        <BodySmallRegular color="$gray600">
                                            {account.type}
                                        </BodySmallRegular>
                                        <BodyMediumRegular>
                                            {formatDollars(account.balance)}
                                        </BodyMediumRegular>
                                    </YStack>
                                ),
                            )}
                        </YStack>

                        <YStack gap={12}>
                            <H5Emphasized>Contributions</H5Emphasized>
                            <BodyMediumRegular
                                color={
                                    RETIREMENT_CALCULATOR_COLORS.CONTRIBUTIONS
                                }
                            >
                                {formatDollars(
                                    currentYearData.totalContributions,
                                )}
                            </BodyMediumRegular>
                        </YStack>

                        <YStack gap={12}>
                            <H5Emphasized>Investment Growth</H5Emphasized>
                            <BodyMediumRegular
                                color={RETIREMENT_CALCULATOR_COLORS.GROWTH}
                            >
                                {formatDollars(currentYearData.totalGrowth)}
                            </BodyMediumRegular>
                        </YStack>

                        <YStack gap={12}>
                            <H5Emphasized>Social Security</H5Emphasized>
                            <BodyMediumRegular
                                color={
                                    RETIREMENT_CALCULATOR_COLORS.SOCIAL_SECURITY
                                }
                            >
                                {formatDollars(
                                    currentYearData.totalSocialSecurity,
                                )}
                            </BodyMediumRegular>
                        </YStack>

                        <YStack gap={12}>
                            <H5Emphasized>Distributions</H5Emphasized>
                            <BodyMediumRegular
                                color={
                                    RETIREMENT_CALCULATOR_COLORS.DISTRIBUTIONS
                                }
                            >
                                {formatDollars(
                                    currentYearData.totalDistributions,
                                )}
                            </BodyMediumRegular>
                        </YStack>

                        <YStack gap={12}>
                            <H5Emphasized>Taxes</H5Emphasized>
                            <YStack gap={4}>
                                <XStack justifyContent="space-between">
                                    <BodySmallRegular>
                                        Distribution Taxes:
                                    </BodySmallRegular>
                                    <BodySmallRegular
                                        color={
                                            RETIREMENT_CALCULATOR_COLORS.TAXES
                                        }
                                    >
                                        {formatDollars(
                                            currentYearData.totalDistributionTaxes,
                                        )}
                                    </BodySmallRegular>
                                </XStack>
                                <XStack justifyContent="space-between">
                                    <BodySmallRegular>
                                        SS Taxes:
                                    </BodySmallRegular>
                                    <BodySmallRegular
                                        color={
                                            RETIREMENT_CALCULATOR_COLORS.TAXES
                                        }
                                    >
                                        {formatDollars(
                                            currentYearData.totalSocialSecurityTaxes,
                                        )}
                                    </BodySmallRegular>
                                </XStack>
                                <XStack justifyContent="space-between">
                                    <BodyMediumEmphasized>
                                        Total:
                                    </BodyMediumEmphasized>
                                    <BodyMediumEmphasized
                                        color={
                                            RETIREMENT_CALCULATOR_COLORS.TAXES
                                        }
                                    >
                                        {formatDollars(
                                            currentYearData.totalTaxes,
                                        )}
                                    </BodyMediumEmphasized>
                                </XStack>
                            </YStack>
                        </YStack>

                        <YStack gap={12}>
                            <H5Emphasized>Retirement Expenses</H5Emphasized>
                            <YStack gap={4}>
                                <XStack justifyContent="space-between">
                                    <BodySmallRegular>
                                        Base Retirement Expenses:
                                    </BodySmallRegular>
                                    <BodySmallRegular
                                        color={
                                            RETIREMENT_CALCULATOR_COLORS.EXPENSES
                                        }
                                    >
                                        {formatDollars(
                                            currentYearData.retirementExpenses,
                                        )}
                                    </BodySmallRegular>
                                </XStack>
                                <XStack justifyContent="space-between">
                                    <BodySmallRegular>
                                        Retirement Healthcare:
                                    </BodySmallRegular>
                                    <BodySmallRegular
                                        color={
                                            RETIREMENT_CALCULATOR_COLORS.HEALTHCARE
                                        }
                                    >
                                        {formatDollars(
                                            currentYearData.retirementHealthcareCosts,
                                        )}
                                    </BodySmallRegular>
                                </XStack>
                                <XStack justifyContent="space-between">
                                    <BodySmallRegular>
                                        Long Term Care:
                                    </BodySmallRegular>
                                    <BodySmallRegular
                                        color={
                                            RETIREMENT_CALCULATOR_COLORS.LONG_TERM_CARE
                                        }
                                    >
                                        {formatDollars(
                                            currentYearData.longTermCareCosts,
                                        )}
                                    </BodySmallRegular>
                                </XStack>
                                <XStack justifyContent="space-between">
                                    <BodyMediumEmphasized>
                                        Total Retirement Expenses:
                                    </BodyMediumEmphasized>
                                    <BodyMediumEmphasized
                                        color={
                                            RETIREMENT_CALCULATOR_COLORS.EXPENSES
                                        }
                                    >
                                        {formatDollars(
                                            currentYearData.totalRetirementExpenses,
                                        )}
                                    </BodyMediumEmphasized>
                                </XStack>
                            </YStack>
                        </YStack>
                    </YStack>
                )}
            </YStack>
        </XStack>
    );
};
